import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileDown, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorText, openFile, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Button, ButtonLink, Card, CardBody, CardHeader, DateInput, EmptyState, ErrorBox, Field, Input, Loading, PageHeader, Pagination, Table, Td, Th, Tr, useToast } from '../../components/ui';
import { formatDate, METHOD_LABELS } from '../../lib/format';

export function DisposalListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const pageSize = 50;

  const list = useQuery({
    queryKey: ['disposals', 'list', { from, to, q, page }],
    queryFn: () => unwrap(api.GET('/api/disposals', { params: { query: { from: from || undefined, to: to || undefined, q: q || undefined, page, page_size: pageSize } } })),
    placeholderData: keepPreviousData,
  });

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ format: 'pdf', ...(from ? { from } : {}), ...(to ? { to } : {}) });
      await openFile(`/api/reports/disposals?${qs}`, { download: `Sammelprotokoll-Verschrottung-${from || 'alle'}-bis-${to || 'heute'}.pdf` });
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Verschrottungen"
        subtitle="Alle Vorgänge mit Protokollen und Entsorgungsnachweisen"
        actions={
          can('asset:dispose') && (
            <ButtonLink to="/disposals/new" variant="primary" icon={<Plus className="size-4" />}>
              Neue Verschrottung
            </ButtonLink>
          )
        }
      />

      <Card className="mb-4">
        <CardHeader title="Zeitraum" description="Filtert die Liste und das Sammelprotokoll für die Buchhaltung." />
        <CardBody className="flex flex-wrap items-end gap-3">
          <Field label="Von">{(p) => <DateInput {...p} value={from} onChange={(e) => (setFrom(e.target.value), setPage(1))} />}</Field>
          <Field label="Bis">{(p) => <DateInput {...p} value={to} onChange={(e) => (setTo(e.target.value), setPage(1))} />}</Field>
          <form
            className="relative min-w-60 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              setQ(term.trim());
              setPage(1);
            }}
          >
            <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-slate-400" />
            <Input className="pl-8" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Vorgangs-/Inventarnummer, Grund …" aria-label="Suche" />
          </form>
          <Button icon={<FileDown className="size-4" />} loading={downloading} onClick={downloadReport}>
            Sammelprotokoll (PDF)
          </Button>
        </CardBody>
      </Card>

      <Card>
        {list.isPending ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox error={list.error} className="m-4" />
        ) : list.data.data.length === 0 ? (
          <EmptyState title="Keine Verschrottungen im Zeitraum" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Vorgang</Th>
                  <Th>Datum</Th>
                  <Th>Geräte</Th>
                  <Th>Grund</Th>
                  <Th>Art</Th>
                  <Th className="text-right">Verschrottet</Th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((d) => (
                  <Tr key={d.id} onClick={() => navigate(`/disposals/${d.disposal_number}`)}>
                    <Td className="font-mono font-medium whitespace-nowrap text-brand-700">{d.disposal_number}</Td>
                    <Td className="whitespace-nowrap">{formatDate(d.disposal_date)}</Td>
                    <Td>
                      {d.primary_assets.map((a) => (
                        <div key={a.id}>
                          <span className="font-mono">{a.asset_number}</span> {a.name}
                        </div>
                      ))}
                    </Td>
                    <Td className="max-w-xs truncate">{d.reason}</Td>
                    <Td>{METHOD_LABELS[d.method] ?? d.method}</Td>
                    <Td className="text-right">{d.disposed_count}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={pageSize} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
